import type { Assignee, ThreadMessage } from '../types/whatsapp';

// Pure rules of the team composer: what the box and the take/return buttons allow, the
// idempotency of sends and the wording of failures. The provider wires them to the API.

/** The backend rejects bodies longer than this many characters (inbox.maxBodyRunes). */
export const MAX_BODY = 4096;

/** Characters as the backend counts them (code points, not UTF-16 units). */
export const charCount = (s: string): number => [...s].length;

// ─── Drafts and idempotency ───────────────────────────────────────────────────

/** `pending*` is the last attempt sent for this conversation whose outcome is not known. */
export interface Draft {
  text: string;
  pendingClientId: string | null;
  pendingText: string | null;
}

export const emptyDraft = (): Draft => ({ text: '', pendingClientId: null, pendingText: null });

/**
 * The client_message_id for sending `text`. It is reused only when the text is the
 * same as the attempt whose outcome is unknown (a timeout that may have succeeded:
 * the server then returns the stored message instead of sending again); a changed
 * text is a different message and gets a new id.
 */
export const clientIdFor = (draft: Draft, text: string, newId: () => string): string =>
  draft.pendingClientId !== null && draft.pendingText === text ? draft.pendingClientId : newId();

/**
 * Handles a message that arrived with the id of the draft's last attempt: that send went
 * through. The text is cleared only when it is still the text that was sent; if the user
 * edited it meanwhile, only the pending attempt is forgotten.
 */
export const reconcileDraft = (draft: Draft, messages: ReadonlyArray<ThreadMessage>): Draft => {
  if (draft.pendingClientId === null) return draft;
  if (!messages.some((m) => m.client_message_id === draft.pendingClientId)) return draft;
  if (draft.text.trim() === draft.pendingText) return emptyDraft();
  return { text: draft.text, pendingClientId: null, pendingText: null };
};

/** A UUID for client_message_id; randomUUID only exists in secure contexts (not http://<LAN IP>). */
export const newClientId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10).join('')}`;
};

// ─── What the composer allows ─────────────────────────────────────────────────

export interface ComposerInput {
  assignedTo: Assignee;
  /** From the conversation detail; null while it is loading. */
  canReply: boolean | null;
  /** The 24 h window is still open on the browser clock (the server flag goes stale while the page is open). */
  windowOpen: boolean;
  accountConnected: boolean;
  /** First name of the patient, for the closed-window hint. */
  contactName: string;
}

export type ComposerMode = 'loading' | 'disconnected' | 'closed' | 'take' | 'team';

export interface ComposerState {
  mode: ComposerMode;
  /** The text box accepts input. */
  canType: boolean;
  placeholder: string;
  hint: string;
  /** "Tomar conversación" is usable. */
  canTake: boolean;
  /** Why "Tomar conversación" is not usable, when the reason is worth showing. */
  takeHelper: string | null;
  /** "Devolver al asistente" is usable. */
  canReturn: boolean;
}

export const composerState = (i: ComposerInput): ComposerState => {
  const team = i.assignedTo === 'human-agent';
  if (i.canReply === null) {
    return { mode: 'loading', canType: false, placeholder: '', hint: '', canTake: false, takeHelper: null, canReturn: false };
  }
  // The backend answers account_not_connected to sends, retries and assignment changes.
  if (!i.accountConnected) {
    return {
      mode: 'disconnected',
      canType: false,
      placeholder: 'No se pueden enviar mensajes sin conexión',
      hint: 'Vuelve a conectar WhatsApp para escribir de nuevo.',
      canTake: false,
      takeHelper: null,
      canReturn: false,
    };
  }
  if (!i.canReply || !i.windowOpen) {
    const who = i.contactName.trim() || 'el paciente';
    return {
      mode: 'closed',
      canType: false,
      placeholder: 'No puedes escribir mientras la ventana esté cerrada',
      hint: `Pasaron más de 24 h desde el último mensaje de ${who}. La ventana se reabre cuando escriba.`,
      canTake: false,
      takeHelper: team ? null : 'No disponible: venció la ventana de 24 h',
      canReturn: team,
    };
  }
  return team
    ? {
        mode: 'team',
        canType: true,
        placeholder: 'Escribe una respuesta como equipo',
        hint: 'Asignada al equipo: el asistente no responde hasta que la devuelvas o venza la ventana.',
        canTake: false,
        takeHelper: null,
        canReturn: true,
      }
    : {
        mode: 'take',
        canType: true,
        placeholder: 'Escribe para tomar la conversación',
        hint: 'Si envías un mensaje, la conversación pasa al equipo y el asistente deja de responder.',
        canTake: true,
        takeHelper: null,
        canReturn: false,
      };
};

/** Send is allowed: the box is usable, the text is not blank and fits, and no send is running. */
export const canSend = (state: ComposerState, text: string, sending: boolean): boolean => {
  if (!state.canType || sending) return false;
  const trimmed = text.trim();
  return trimmed !== '' && charCount(trimmed) <= MAX_BODY;
};

// ─── Failures ─────────────────────────────────────────────────────────────────

/**
 * The line shown under the composer for an error `code` of the send, retry and
 * assignment endpoints. Null means nothing to show (the poll corrects the screen).
 */
export type ActionKind = 'send' | 'retry' | 'assign';

export const actionErrorText = (code: string, fallback: string, kind: ActionKind = 'send'): string | null => {
  switch (code) {
    case 'already_assigned':
      return null;
    case 'window_closed':
      return 'La ventana de 24 h se cerró. No se envió el mensaje.';
    case 'account_not_connected':
      return 'WhatsApp está desconectado. No se envió el mensaje.';
    case 'message_empty':
      return 'Escribe un mensaje antes de enviar.';
    case 'message_too_long':
      return `El mensaje es demasiado largo (máximo ${MAX_BODY} caracteres).`;
    case '':
      // No code: the request did not complete. For a send it may or may not have gone through.
      if (kind === 'assign') return 'No se pudo cambiar la asignación. Revisa tu conexión e intenta de nuevo.';
      if (kind === 'retry') return 'No se pudo confirmar el reintento. Revisa el hilo antes de volver a intentarlo.';
      return 'No se pudo confirmar el envío. Revisa el hilo; si lo reenvías, no se duplicará.';
    default:
      return fallback;
  }
};
